import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Accordion } from 'cd-eft/components/common/Accordion';
import { ButtonBlock } from '../../common/ButtonBlock/ButtonBlock';
import { UGODesignVM } from './UGODesignVM';
import { ActionsMenu } from '../../common/ActionsMenu';
import './UGODesign.scss';
import { DynamicUGOHub } from 'cd-eft/models/Stores/DynamicProfile/UGO/DynamicUGOHub';
import { DynamicProfileStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileStoreBase';
import { DynamicTableStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicTableStoreBase';
import { ProfileStoreType } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileEnums';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import { useHistory } from 'react-router-dom';
import { DynamicUGOProfile } from 'cd-eft/components/common/DynamicProfile/DynamicUGOProfile';
import { Container, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import { ReactTableThead } from '../../common/ReachTable/ReachTable';
import { StaticTooltip, TooltipDirections } from 'cd-eft/components/common/StaticTooltip/StaticTooltip';
import { mdiClose, mdiDelete, mdiHelpCircleOutline } from '@mdi/js';
import stores from 'cd-eft/models/Stores';
import { DeleteElement } from '../../common/DeleteElement';
import { FixtureModal } from '../FixtureModal/FixtureModal';
import { Loader } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';
import { Popup } from 'cd-eft/components/common/Popup';
import { ProfileSettingsContainer } from '../../common/ProfileSettingsContainer/ProfileSettingsContainer';
import Draggable from 'react-draggable';
import { PipeInputs } from '../UGOPipeInputs/UGOPipeInputs';
import Icon from '@mdi/react';
import { UserLeavingWarning } from 'cd-eft/components/common/UserLeavingWarning/UserLeavingWarning';
import { OnClickReachInformation } from '../../common/OnClickReachInformation/OnClickReachInformation';

interface IUgoProps {
  addUgoEdits?: any;
  mapView: any;
}

const { ugoDesignStore, designStore } = stores.AppStore;

const UGODesignComponent = (props: IUgoProps) => {
  const history = useHistory();

  let profileHub: DynamicUGOHub;
  const profileStoreRef = useRef<DynamicProfileStoreBase>();
  const profileTableStoreRef = useRef<DynamicTableStoreBase>();

  if (!profileTableStoreRef.current && !profileStoreRef.current) {
    profileHub = new DynamicUGOHub();
    profileHub.createStore(ProfileStoreType.MAP, { mapView: EngineeringMapService.instance?.mapView });
    profileHub.createStore(ProfileStoreType.PROFILE);
    profileStoreRef.current = profileHub.ProfileStore;
    profileHub.createStore(ProfileStoreType.TABLE);
    profileTableStoreRef.current = profileHub.TableStore;
  }

  const vmREF = useRef<UGODesignVM>();
  if (vmREF.current?.history) vmREF.current.history = history
  if (!vmREF.current) {
    vmREF.current = new UGODesignVM(
      {
        ...props,
        tableStoreRef: profileTableStoreRef.current,
        profileStoreRef: profileStoreRef.current
      },
    );
  }
  const vm = vmREF.current;

  useEffect(() => {
    ugoDesignStore.ugoHistory = history;
    vm.onMount();
    return () => {
      vm.unMount();
    };
  }, []);

  // ************** Profile Setup ************** //
  // Need to check if UI is fully rendered before drawing profile
  const [uiReady, setUiReady] = useState(false);
  const profileContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkDimensions = () => {
      if (profileContainerRef.current) {
        const { width, height } = profileContainerRef.current.getBoundingClientRect();

        if (width > 0 && height > 0) {
          setUiReady(true);
          return;
        }
      }

      requestAnimationFrame(checkDimensions);
    };

    checkDimensions();
  }, []);
  // ************ End Profile Setup ************ //

  useEffect(() => {
    // NOTE: This useEffect is reserved for updating the channel profile when the user clicks "Clear All" or "Undo" in the Pending Edit Manager.
    const resetValues = async () => {
      if (designStore.toggleResetValues) {
        await profileStoreRef.current?.createChannelFromSavedUgo();
        designStore.toggleResetValues = false;
      }
    };
    resetValues();
  }, [designStore.toggleResetValues]);

  return (
    <>
      <div></div> {/*Temporary fix for excessive padding-left for first-child (in TerraceDesign.scss), until terrace styles in UGOContainer.tsx are renamed.*/}
      <div className="ugo-design-container separator-container" id="separator-container">
        <div ref={profileContainerRef} id="separator-top-container">
          {/* *****Profile****** */}
          <Accordion
            key={`ugo-profile-tab-content`}
            className="accordion-wrapper profile-info"
            headers={['Profile']}
            expanded={true}
            light={true}
            iconPathQ={true}
            onClickInformationToggle={vm.onClickProfileInformationToggle}
            actionButton={
              <ActionsMenu item="item" elementActions={vm.profileActions} disabled={false}>
                Action
              </ActionsMenu>
            }
            onToggle={vm.removeHeights}
          >
            <>
              <DynamicUGOProfile profileStore={profileStoreRef.current} editable={false} generateInitialProfiles={vm.loadUgoDataAndProfiles} uiReady={uiReady} />
              <div id="UGO-design-profile-btns">
                <ButtonBlock buttons={vm.profileButtons} />
              </div>
            </>
          </Accordion>
        </div>

        <div id="separator"></div>

        <div id="separator-bottom-container">
          {/* *****UGO Pipeline Table****** */}
          <Accordion
            className="accordion-wrapper profile-reaches-info pb-0"
            headers={['UGO Pipeline']}
            expanded={true}
            light={true}
            onToggle={vm.removeHeights}
          >
            <div className="reach-table-container">
              <div className="tab-content">
                <div className="tab-pane active">
                  <div className="usa-table-container--scrollable">
                    <Table className="usa-table usa-table--borderless reach-table">
                      <ReactTableThead el={vm.ugoTableColumns} selectAll={vm.selectAll} activeTab={undefined} designType="UGO" />
                      <tbody>
                        {ugoDesignStore.ugoDesignObject.ugoDesignStations && ugoDesignStore.ugoDesignObject.ugoDesignStations.length > 0 && ugoDesignStore.ugoDesignObject.ugoDesignStations.map((rowData, idx) => (
                          <tr
                            id={`row-ugoDesignStation-${idx}`}
                            key={`row-${idx}`}
                            onMouseEnter={(e) => vm.handleRowMouseEnter(e, idx)}
                            onMouseLeave={(e) => vm.handleRowMouseLeave(e, idx)}
                          >
                            {/* Select/Deselect Checkbox */}
                            <td className="narrow-td">
                              {!['P-0'].includes(rowData.reachId) && (
                                <input
                                  type="checkbox"
                                  aria-label={`select ${rowData.reachId}`}
                                  checked={vm.checkedRowStations.includes(rowData.reachId)}
                                  onChange={(e) => {
                                    // vm.handleCheckboxChange(e, idx);
                                  }}
                                />
                              )}
                            </td>
                            {/* Status */}
                            <td>
                              <Status rowData={rowData} idx={idx} onClick={vm.onClickInformationToggle} />
                            </td>
                            {/* Dynamic Columns based on el.defaultCols */}
                            {vm.ugoTableColumns.defaultCols.map((col, colIdx) => {
                              const [isEditable, value] = vm.setValue(col, rowData);
                              return (
                                <td key={`cell-${idx}-${colIdx}`}>
                                  {isEditable && rowData.reachId !== 'P-0' ? (
                                    <input
                                      className={`usa-input ${vm.changedInputValue && vm.formValidationUgoDesign.invalidFields.some(
                                        field => field.fieldName === col.name && field.index === idx
                                      ) ? 'usa-input--error' : ''}`}
                                      type="text"
                                      aria-label={col.name}
                                      value={value}
                                      onChange={(e) => vm.handleInputChange(e, idx, col.name)}
                                      onBlur={(e) => vm.handleTableInputChange(e, idx, col.name)}
                                    />
                                  ) : value}
                                </td>
                              );
                            })}
                            {/* Action Column */}
                            <td className={`ugo-action-menu narrow-td action-td`}>
                              <ActionsMenu
                                i={idx}
                                item={rowData}
                                elementActions={
                                  vm.setElementActions(rowData)
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          </Accordion>
        </div>
        <div id="ugo-design-btns" className="pr-3 pb-2 border-top">
          <ButtonBlock buttons={vm.ugoTableButtons} />
        </div>
        {vm.loading && <Loader message={''} />}
      </div>

      {vm.onClickProfileInformation ? (
        <Modal
          isOpen={vm.onClickProfileInformation}
          toggle={vm.onClickProfileInformationToggle}
          centered={true}
          className="eft-action-modal"
          aria-label={vm.headerTitle}
        >
          <ModalHeader toggle={vm.onClickProfileInformationToggle} className='header-info-detail'>
            <div className="display-flex flex-align-center">
              <strong>{`Information Details on ${vm.headerTitle}`}</strong>
            </div>
            {/* <a className="href-design-detail105" href="/design/designdetails" target="_blank">
              <strong>Open Information in a New Window</strong>
            </a> */}
          </ModalHeader>
          <ModalBody>
            {/* Add profile information */}
          </ModalBody>
          <ModalFooter>
            <button className="usa-button usa-button--outline" onClick={vm.onClickProfileInformationToggle}>
              Close
            </button>
          </ModalFooter>
        </Modal>
      ) : null}

      {vm.deleteStationDialogIsOpen && (
        <DeleteElement
          trigger={vm.deleteStationDialogIsOpen}
          setTrigger={vm.toggleDeleteStationDialog}
          element="ugo station"
          name={vm.ugoDesignStationToDelete.reachId}
          action={vm.deleteUgoDesignStation}
        />
      )}

      {vm.showFixtureModal && vm.fixtureModalVM && (
        <FixtureModal
          vm={vm.fixtureModalVM}
          onApply={vm.applyFixtureUpdates}
          onClose={vm.closeFixtureModal}
        />
      )}

      {vm.redigitizeReachesDialogIsOpen && (
        <Popup
          trigger={vm.redigitizeReachesDialogIsOpen}
          setTrigger={vm.toggleRedigitizeUgoPipeDialog}
          title={`Replace Channel Profile?`}
          icon={mdiDelete}
          handleCancel={vm.toggleRedigitizeUgoPipeDialog}
          cancelText="Close"
          handleOK={vm.digitizeUgoPipe}
          okText="Ok"
        >
          <p>{vm.notification.redigitizeReaches}</p>
        </Popup>
      )}

      <UserLeavingWarning
        when={designStore.ugoGroupsLength > 0}
        section="UGO Design "
        onOK={vm.userLeavingWarningOnOk}
        okText="Return"
        // cancelText="Cancel"
        // onCancel={vm.userLeavingWarningOnCancel}
        text={vm.notification.userLeavingWarning}
      />

      {vm.onClickInformation ? (
        <OnClickReachInformation
          onClickInformation={vm.onClickInformation}
          onClickInformationToggle={vm.onClickInformationToggle}
          errorCode={vm.errorCode}
          selectedReachId={vm.selectedReachId}
          errorMessage={vm.errorMessage}
        />
      ) : null}

      {vm.profileSettingsForm.profileSettingPopUpActive &&
        <Modal
          className='ugo-modal-container'
          isOpen={vm.profileSettingsForm.profileSettingPopUpActive}
          toggle={vm.handleCancelProfileSettings}
          centered={true}
          fade={false}
        >
          <Draggable
            axis="both"
            positionOffset={{ x: '-50%', y: '-50%' }}
            cancel="input"
          >
            <div className='profile-settings'>
              <ProfileSettingsContainer
                profileSettingsForm={vm.profileSettingsForm}
                formValidation={vm.formValidation}
                toggleFineGrid={vm.toggleFineGrid}
                handleCancelProfileSettings={vm.handleCancelProfileSettings}
                handleSaveProfileSettings={vm.handleSaveProfileSettings}
                title={'Profile Settings'}
                saveButtonText={'Apply'}
                parentType='UGO'
              />
            </div>
          </Draggable>
        </Modal>
      }

      {vm.showEditPipeModal && <Draggable axis="both" positionOffset={{ x: '-50%', y: '-50%' }} cancel="input,select">
        <div className={`ugo-fixture-modal ugo-pipeline-default`}>
          <div id="tcs-header">
            <h1 className="h5 mt-1 mb-1 ml-2">Edit Pipe</h1>
            <button className="tcs-close" aria-label="close tool" onClick={() => vm.toggleEditPipeModal()}>
              <Icon path={mdiClose} title="Close" size={1} />
            </button>
          </div>
          <Container className="p-3">
            <div className="p-3">
              <PipeInputs />
            </div>
          </Container>
          <ButtonBlock
            className="p-2 border-top"
            buttons={[
              { title: 'Close', disabled: false, onClick: () => vm.toggleEditPipeModal(), className: 'usa-button--outline' },
              { title: 'Apply', disabled: false, onClick: vm.applyPipeUpdates },
            ]}
          />
        </div>
      </Draggable>}
    </>
  );
};

export const UGODesign = observer(UGODesignComponent);

interface IStatus {
  rowData: any,
  idx: number,
  onClick: any,
  tooltipSide?: TooltipDirections,
  icon?: string,
  tooltipMessage?: string
}

export const Status = ({ rowData, idx, onClick, tooltipSide, icon, tooltipMessage }: IStatus) => {
  return (
    <div className='status-container'>
      <div
        className={rowData.status && !['P-0'].includes(rowData.reachId) ? `status-label status--${rowData.status.replace(" ", "_")}` : ""}
        data-testid={`status-${idx}`}
      >
        {!['P-0'].includes(rowData.reachId) && rowData.status === 'WARNING_STATUS'
          ? (<span> WARNING </span>)
          : (<span> {['P-0'].includes(rowData.reachId)
            ? ""
            : rowData.status?.replace("_STATUS", "")}</span>)
        }
      </div>
      {rowData.status &&
        !['P-0'].includes(rowData.reachId) &&
        ['ERROR', 'WARNING'].includes(rowData.status) && (
          <div
            className="question-button"
            title=""
            onClick={() => onClick(rowData.reachId)}
          >
            <StaticTooltip
              tooltipMessage={tooltipMessage ? tooltipMessage : "More Information"}
              tooltipSide={tooltipSide ?? "bottom"}
              id={`tooltip-${idx}`}
              icon={icon ?? mdiHelpCircleOutline}
              title=""
            />
          </div>
        )}
    </div>
  )
}